import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/CustomAuthContext';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import LoggingPanel from './LoggingPanel';
import AdminDataManagement from './AdminDataManagement';
import FeatureFlagsAdmin from './FeatureFlagsAdmin';
import './AdminPanel.css';

interface User {
  id: number;
  username: string;
  email: string;
  role: 'user' | 'checker' | 'admin';
  createdAt: string;
  lastLogin?: string;
  status: 'active' | 'suspended';
}

interface SystemStats {
  totalUsers: number;
  totalSubmissions: number;
  pendingReviews: number;
  approvedSubmissions: number;
  rejectedSubmissions: number;
  totalPokemonData: number;
  totalItemData: number;
}

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'logging' | 'data-management' | 'map-editor' | 'feature-flags'>('overview');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [systemSettings, setSystemSettings] = useState<Record<string, string>>({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState<any>(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  // Load real data from backend
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const [usersData, statsData] = await Promise.all([
          adminAPI.getUsers(),
          adminAPI.getSystemStats()
        ]);

        // Load system settings separately
        try {
          const settingsData = await adminAPI.getSystemSettings();
          setSystemSettings(settingsData);
        } catch (error) {
          console.error('Failed to load system settings:', error);
        }

        // Transform users data to match UI interface
        const transformedUsers: User[] = usersData.map(u => ({
          id: u.user_id,
          username: u.username,
          email: u.email || '',
          role: u.role,
          createdAt: new Date().toISOString(), // Use current date for now
          status: u.account_status === 'Active' ? 'active' : 'suspended'
        }));

        // Transform stats data to match UI interface
        const transformedStats: SystemStats = {
          totalUsers: statsData.total_users,
          totalSubmissions: statsData.total_item_sales + statsData.total_pokemon_sales,
          pendingReviews: statsData.pending_item_reviews + statsData.pending_pokemon_reviews,
          approvedSubmissions: statsData.approved_items + statsData.approved_pokemon,
          rejectedSubmissions: 0, // Add rejected count if needed
          totalPokemonData: statsData.total_pokemon_sales,
          totalItemData: statsData.total_item_sales
        };

        setUsers(transformedUsers);
        setSystemStats(transformedStats);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        // Fall back to empty data
        setUsers([]);
        setSystemStats({
          totalUsers: 0,
          totalSubmissions: 0,
          pendingReviews: 0,
          approvedSubmissions: 0,
          rejectedSubmissions: 0,
          totalPokemonData: 0,
          totalItemData: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [users, searchTerm]);

  const handleRoleChange = async (userId: number, newRole: 'user' | 'checker' | 'admin') => {
    setActionLoading(true);

    try {
      await adminAPI.updateUserRole(userId, newRole);

      // Update local state
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      setSelectedUser(null);

    } catch (error) {
      console.error('Failed to update user role:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUserStatusChange = async (userId: number, status: 'active' | 'suspended') => {
    setActionLoading(true);

    try {
      // Convert to backend format
      const backendStatus = status === 'active' ? 'Active' : 'Suspended';
      await adminAPI.updateUserStatus(userId, backendStatus);

      // Update local state
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status } : u));
      setSelectedUser(prev => prev ? { ...prev, status } : null);

    } catch (error) {
      console.error('Failed to update user status:', error);
      alert('Failed to update user status. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkipReviewToggle = async () => {
    setSettingsLoading(true);

    try {
      const currentValue = systemSettings.skip_review_enabled === 'true';
      const newValue = !currentValue;

      await adminAPI.updateSystemSetting('skip_review_enabled', newValue.toString());

      // Update local state
      setSystemSettings(prev => ({
        ...prev,
        skip_review_enabled: newValue.toString()
      }));

    } catch (error) {
      console.error('Failed to update skip review setting:', error);
      alert('Failed to update skip review setting. Please try again.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDetectDuplicates = async () => {
    setDuplicateLoading(true);

    try {
      const duplicates = await adminAPI.detectDuplicates();
      setDuplicateResults(duplicates);

    } catch (error) {
      console.error('Failed to detect duplicates:', error);
      alert('Failed to detect duplicates. Please try again.');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleDeleteDuplicate = async (type: 'item' | 'pokemon', id: number) => {
    if (!window.confirm(`Are you sure you want to delete this ${type} entry? This action cannot be undone.`)) {
      return;
    }

    try {
      await adminAPI.deleteDuplicateEntry(type, id);
      // Refresh duplicates after deletion
      await handleDetectDuplicates();
    } catch (error) {
      console.error('Failed to delete duplicate entry:', error);
      alert('Failed to delete entry. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return '#f44336';
      case 'checker': return '#ff9800';
      case 'user': return '#4caf50';
      default: return '#666';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? '#4caf50' : '#f44336';
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="admin-panel-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You must be an administrator to access this panel.</p>
        </div>
      </div>
    );
  }

  return (
  <div className="admin-panel-container">
      <header className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>⚙️ Admin Panel</h1>
            <p>System administration and user management</p>
          </div>
        </div>
      </header>

      <main className="admin-main">
        <div className="filter-buttons">
          {[
            { id: 'overview', label: '📊 Overview' },
            { id: 'users', label: '👥 Users' },
            { id: 'data-management', label: '🗃️ Data Management' },
            { id: 'logging', label: '📋 Logging' },
            { id: 'map-editor', label: '🗺️ Map Region Editor' },
            { id: 'feature-flags', label: '🚩 Feature Flags' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`admin-panel-filter-button ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="admin-panel-loading">
            <div className="admin-panel-loading-spinner"></div>
            <p>Loading admin data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && systemStats && (
              <div className="overview-content">
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">👥</div>
                    <div className="stat-info">
                      <h3>{systemStats.totalUsers}</h3>
                      <p>Total Users</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">📝</div>
                    <div className="stat-info">
                      <h3>{systemStats.totalSubmissions}</h3>
                      <p>Total Submissions</p>
                    </div>
                  </div>

                  <div className="stat-card pending">
                    <div className="stat-icon">⏳</div>
                    <div className="stat-info">
                      <h3>{systemStats.pendingReviews}</h3>
                      <p>Pending Reviews</p>
                    </div>
                  </div>

                  <div className="stat-card approved">
                    <div className="stat-icon">✅</div>
                    <div className="stat-info">
                      <h3>{systemStats.approvedSubmissions}</h3>
                      <p>Approved</p>
                    </div>
                  </div>

                  <div className="stat-card rejected">
                    <div className="stat-icon">❌</div>
                    <div className="stat-info">
                      <h3>{systemStats.rejectedSubmissions}</h3>
                      <p>Rejected</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">🔴</div>
                    <div className="stat-info">
                      <h3>{systemStats.totalPokemonData}</h3>
                      <p>Pokemon Data</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">🎒</div>
                    <div className="stat-info">
                      <h3>{systemStats.totalItemData}</h3>
                      <p>Item Data</p>
                    </div>
                  </div>
                </div>

                {/* System Settings Section */}
                <div className="settings-section">
                  <h3>System Settings</h3>
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Skip Review for Admin/Checker Submissions</h4>
                      <p>When enabled, submissions from admins and checkers will be automatically approved and bypass the review process.</p>
                    </div>
                    <button
                      className={`toggle-button ${systemSettings.skip_review_enabled === 'true' ? 'enabled' : 'disabled'}`}
                      onClick={handleSkipReviewToggle}
                      disabled={settingsLoading}
                    >
                      <div className="toggle-slider">
                        <div className="toggle-knob"></div>
                      </div>
                      <span className="toggle-label">
                        {settingsLoading ? 'Updating...' : (systemSettings.skip_review_enabled === 'true' ? 'Enabled' : 'Disabled')}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="users-content">
                <div className="users-header">
                  <h3>User Management</h3>
                  <div className="users-stats">
                    <span className="user-count">Total: {users.length} users</span>
                    {searchTerm && (
                      <span className="filtered-count">Showing: {filteredUsers.length}</span>
                    )}
                  </div>
                </div>

                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Search users by username or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="clear-search"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="users-table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(userData => (
                        <tr key={userData.id}>
                          <td>{userData.username}</td>
                          <td>{userData.email}</td>
                          <td>
                            <span
                              className="role-badge"
                              style={{ backgroundColor: getRoleColor(userData.role) }}
                            >
                              {userData.role}
                            </span>
                          </td>
                          <td>
                            <span
                              className="status-badge"
                              style={{ backgroundColor: getStatusColor(userData.status) }}
                            >
                              {userData.status}
                            </span>
                          </td>
                          <td>{formatDate(userData.createdAt)}</td>
                          <td>{userData.lastLogin ? formatDate(userData.lastLogin) : 'Never'}</td>
                          <td>
                            <button
                              onClick={() => setSelectedUser(userData)}
                              className="manage-button"
                              disabled={userData.username === user.username}
                            >
                              Manage
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'data-management' && (
              <div className="data-management-content">
                <div className="data-management-header">
                  <h3>🗃️ Data Management</h3>
                  <p>Manage and maintain the database integrity</p>
                </div>

                {/* Data Management Section */}
                <div className="settings-section">
                  <h3>Duplicate Entry Detection</h3>
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Scan for Duplicates</h4>
                      <p>Scan for duplicate entries in Items (price, date, name) and Pokemon (nature, ability, hidden power, IVs, price, date) data.</p>
                    </div>
                    <button
                      className="action-button"
                      onClick={handleDetectDuplicates}
                      disabled={duplicateLoading}
                    >
                      {duplicateLoading ? '🔍 Scanning...' : '🔍 Detect Duplicates'}
                    </button>
                  </div>

                  {duplicateResults && (
                    <div className="duplicate-results">
                      <h4>Duplicate Detection Results</h4>
                      <div className="results-summary">
                        <div className="result-stat">
                          <span className="stat-number">{duplicateResults.items?.length || 0}</span>
                          <span className="stat-label">Item Duplicates</span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-number">{duplicateResults.pokemon?.length || 0}</span>
                          <span className="stat-label">Pokemon Duplicates</span>
                        </div>
                      </div>

                      {duplicateResults.items && duplicateResults.items.length > 0 && (
                        <div className="duplicate-group">
                          <h5>📦 Item Duplicates</h5>
                          <div className="duplicate-list">
                            {duplicateResults.items.slice(0, 10).map((item: any, index: number) => {
                              const latestId = item.item_ids.split(',')[0]; // First ID is the latest
                              const proofUrls = item.proof_urls ? item.proof_urls.split('|||').filter((url: string) => url && url.trim()) : [];
                              return (
                                <div key={index} className="duplicate-item compact">
                                  <div className="item-info">
                                    <span className="item-name">{item.name}</span>
                                    <span className="item-price">${item.price}</span>
                                    <span className="duplicate-count">{item.count} dupes</span>
                                  </div>
                                  <div className="item-actions">
                                    <div className="admin-panel-proof-links-container">
                                      {proofUrls.map((url: string, urlIndex: number) => (
                                        <a key={urlIndex} href={url} target="_blank" rel="noopener noreferrer" className="admin-panel-proof-link">
                                          🔗 {urlIndex + 1}
                                        </a>
                                      ))}
                                    </div>
                                    <span className="item-date">{new Date(item.date).toLocaleDateString()}</span>
                                    <button
                                      className="delete-button small"
                                      onClick={() => handleDeleteDuplicate('item', parseInt(latestId))}
                                      title="Delete latest entry"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            {duplicateResults.items.length > 10 && (
                              <div className="more-results">
                                ... and {duplicateResults.items.length - 10} more
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {duplicateResults.pokemon && duplicateResults.pokemon.length > 0 && (
                        <div className="duplicate-group">
                          <h5>🔴 Pokemon Duplicates</h5>
                          <div className="duplicate-list">
                            {duplicateResults.pokemon.slice(0, 10).map((pokemon: any, index: number) => {
                              const latestId = pokemon.sale_ids.split(',')[0]; // First ID is the latest
                              const proofUrls = pokemon.proof_urls ? pokemon.proof_urls.split('|||').filter((url: string) => url && url.trim()) : [];
                              return (
                                <div key={index} className="duplicate-item pokemon-duplicate compact">
                                  <div className="pokemon-header">
                                    <span className="pokemon-name">{pokemon.pokemon_name}</span>
                                    <span className="pokemon-price">${pokemon.price}</span>
                                    <span className="duplicate-count">{pokemon.count} dupes</span>
                                  </div>
                                  <div className="pokemon-stats-row">
                                    <div className="pokemon-basic">
                                      <span className="stat">Nature: {pokemon.nature}</span>
                                      <span className="stat">Ability: {pokemon.ability}</span>
                                      {pokemon.hidden_power && <span className="stat">HP: {pokemon.hidden_power}</span>}
                                      {pokemon.form && <span className="stat">Form: {pokemon.form}</span>}
                                    </div>
                                    <div className="pokemon-ivs-compact">
                                      <span className="iv-compact">{pokemon.hp_iv || 'X'}</span>
                                      <span className="iv-compact">{pokemon.atk_iv || 'X'}</span>
                                      <span className="iv-compact">{pokemon.def_iv || 'X'}</span>
                                      <span className="iv-compact">{pokemon.spatk_iv || 'X'}</span>
                                      <span className="iv-compact">{pokemon.spdef_iv || 'X'}</span>
                                      <span className="iv-compact">{pokemon.speed_iv || 'X'}</span>
                                    </div>
                                  </div>
                                  <div className="pokemon-footer">
                                    <div className="pokemon-links">
                                      <div className="admin-panel-proof-links-container">
                                        {proofUrls.map((url: string, urlIndex: number) => (
                                          <a key={urlIndex} href={url} target="_blank" rel="noopener noreferrer" className="admin-panel-proof-link">
                                            🔗 {urlIndex + 1}
                                          </a>
                                        ))}
                                      </div>
                                      <span className="pokemon-date">{new Date(pokemon.date).toLocaleDateString()}</span>
                                    </div>
                                    <button
                                      className="delete-button small"
                                      onClick={() => handleDeleteDuplicate('pokemon', parseInt(latestId))}
                                      title="Delete latest entry"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            {duplicateResults.pokemon.length > 10 && (
                              <div className="more-results">
                                ... and {duplicateResults.pokemon.length - 10} more
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}


            {activeTab === 'data-management' && (
              <AdminDataManagement />
            )}

            {activeTab === 'map-editor' && (
              <div className="map-editor-content">
                <h3>🗺️ Map Region Editor</h3>
                <p>Visually map regions on the world map and export region data as JSON.</p>
                <a
                  href="/admin/map-region-editor"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', margin: '16px 0', padding: '10px 18px', background: '#23262f', color: '#fff', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                >
                  Open Map Region Editor
                </a>
                <p style={{ fontSize: '0.95rem', color: '#aaa' }}>Only accessible to admins.</p>
              </div>
            )}

            {activeTab === 'logging' && (
              <LoggingPanel />
            )}

            {activeTab === 'feature-flags' && (
              <FeatureFlagsAdmin />
            )}
          </>
        )}
      </main>

      {selectedUser && (
        <div className="user-modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage User: {selectedUser.username}</h2>
              <button
                onClick={() => setSelectedUser(null)}
                className="close-button"
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="user-info">
                <p><strong>Email:</strong> {selectedUser.email}</p>
                <p><strong>Current Role:</strong> {selectedUser.role}</p>
                <p><strong>Status:</strong> {selectedUser.status}</p>
                <p><strong>Created:</strong> {formatDate(selectedUser.createdAt)}</p>
                <p><strong>Last Login:</strong> {selectedUser.lastLogin ? formatDate(selectedUser.lastLogin) : 'Never'}</p>
              </div>

              <div className="management-actions">
                <div className="action-section">
                  <h4>Change Role</h4>
                  <div className="role-buttons">
                    {['user', 'checker', 'admin'].map(role => (
                      <button
                        key={role}
                        onClick={() => handleRoleChange(selectedUser.id, role as any)}
                        disabled={actionLoading || selectedUser.role === role}
                        className={`role-button ${selectedUser.role === role ? 'current' : ''}`}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="action-section">
                  <h4>Change Status</h4>
                  <div className="status-buttons">
                    <button
                      onClick={() => handleUserStatusChange(selectedUser.id, 'active')}
                      disabled={actionLoading || selectedUser.status === 'active'}
                      className={`status-button active ${selectedUser.status === 'active' ? 'current' : ''}`}
                    >
                      Activate
                    </button>
                    <button
                      onClick={() => handleUserStatusChange(selectedUser.id, 'suspended')}
                      disabled={actionLoading || selectedUser.status === 'suspended'}
                      className={`status-button suspended ${selectedUser.status === 'suspended' ? 'current' : ''}`}
                    >
                      Suspend
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setSelectedUser(null)}
                className="cancel-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
